import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserType } from '@prisma/client';
import { useAuth } from './AuthContext';
import {
  RevaContext,
  type OpenRevaOptions,
  type RevaContextValue,
} from './revaContextValue';

// Re-exported so existing consumers keep working; new code importing only the
// hook should take it from './revaContextValue' to avoid pulling in RevaPanel.
export { useReva } from './revaContextValue';
export type { OpenRevaOptions, RevaContextValue } from './revaContextValue';
import revaBriefingService, {
  RevaBriefing,
} from '../api/services/revaBriefing';

const RevaPanel = React.lazy(() => import('../components/Reva/RevaPanel'));

const BRIEFING_STALE_MS = 60_000;


export const RevaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [briefing, setBriefing] = useState<RevaBriefing | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  // Read inside notifyReply so a reply that lands after a close still counts.
  const isOpenRef = useRef(false);
  const fetchedAt = useRef(0);
  const inFlight = useRef<Promise<void> | null>(null);
  // Identity the current briefing belongs to; a response for a different user is discarded.
  const briefingUserId = useRef<string | undefined>(undefined);
  // Monotonic id of the latest briefing request; a stale request must not touch shared state.
  const requestSeq = useRef(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const notifyReply = useCallback(() => {
    if (isOpenRef.current) return;
    setUnseenCount((count) => count + 1);
  }, []);

  const userId = currentUser?.id;
  // A PBUYER is the other side of one of our seller listings, not a customer: Reva never
  // opens for it. Admins use the internal tools; anonymous visitors get the public chat.
  const available = currentUser?.type !== UserType.PBUYER;
  const canBrief =
    available && Boolean(userId) && currentUser?.type !== UserType.ADMIN;

  const refreshBriefing = useCallback(
    async (force = false) => {
      if (!canBrief) return;
      if (inFlight.current) return inFlight.current;
      if (!force && Date.now() - fetchedAt.current < BRIEFING_STALE_MS) return;

      const requestedFor = userId;
      const seq = ++requestSeq.current;
      const isCurrent = () =>
        requestSeq.current === seq && briefingUserId.current === requestedFor;
      setBriefingLoading(true);
      inFlight.current = revaBriefingService
        .getBriefing()
        .then((data) => {
          if (!isCurrent()) return;
          setBriefing(data);
          fetchedAt.current = Date.now();
        })
        .catch((error) => {
          console.error('Failed to load Reva briefing:', error);
        })
        .finally(() => {
          // A request superseded by a user switch must not clear the newer request's state.
          if (!isCurrent()) return;
          setBriefingLoading(false);
          inFlight.current = null;
        });
      return inFlight.current;
    },
    [canBrief, userId]
  );

  // Everything Reva holds is scoped to one account: close the chat, drop the
  // briefing and any pending question whenever the signed-in user changes
  // (including logout), so one person's offers never linger for the next.
  useEffect(() => {
    briefingUserId.current = userId;
    setIsOpen(false);
    setPendingQuestion(null);
    setBriefing(null);
    setUnseenCount(0);
    fetchedAt.current = 0;
    inFlight.current = null;
    requestSeq.current += 1;
    setBriefingLoading(false);
    if (canBrief) {
      refreshBriefing(true);
    }
  }, [canBrief, userId, refreshBriefing]);

  const openReva = useCallback(
    (options?: OpenRevaOptions) => {
      if (!available) return;
      if (options?.question) {
        setPendingQuestion(options.question);
      }
      setIsOpen(true);
      setUnseenCount(0);
      refreshBriefing();
    },
    [refreshBriefing, available]
  );

  // Dismissal is not disposal: the panel stays mounted, so the thread, the
  // conversation id and any pending action card are still there on reopen.
  const closeReva = useCallback(() => {
    setIsOpen(false);
    setPendingQuestion(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      available,
      openReva,
      closeReva,
      briefing,
      briefingLoading,
      refreshBriefing,
      unseenCount,
      notifyReply,
    }),
    [
      isOpen,
      available,
      openReva,
      closeReva,
      briefing,
      briefingLoading,
      refreshBriefing,
      unseenCount,
      notifyReply,
    ]
  );

  return (
    <RevaContext.Provider value={value}>
      {children}
      {/*
        Mounted for the whole session, not only while it is open: the panel hides
        itself when closed so the conversation survives dismissal, collapse, route
        changes and a resize across placement breakpoints. The key is the only
        thing that resets it, so one account's thread never reaches the next.
      */}
      {available && (
        <Suspense fallback={null}>
          <RevaPanel
            key={userId ?? 'anonymous'}
            open={isOpen}
            onClose={closeReva}
            initialQuestion={pendingQuestion}
            onInitialQuestionConsumed={() => setPendingQuestion(null)}
          />
        </Suspense>
      )}
    </RevaContext.Provider>
  );
};

